import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

export interface CookieConsent {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  version: 1;
}

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    // fbq?: any;
    // _fbq?: any;
    // hj?: any;
    // _hjSettings?: any;
    // clarity?: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  private readonly storageKey = 'mspadek_cookie_consent_v1';
  private readonly forceLoadAllScripts = false; //for tests set `true`,

  private get isProduction(): boolean {
    
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    return ['mspadek.pl', 'www.mspadek.pl'].includes(
      window.location.hostname.toLowerCase(),
    );
  }


  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.initConsentMode();
    }
  }

  getConsent(): CookieConsent | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const raw = localStorage.getItem(this.storageKey);
    return raw ? JSON.parse(raw) : null;
  }

  acceptAll(): void {
    this.saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      version: 1,
    });
  }

  rejectAll(): void {
    this.saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      version: 1,
    });
  }

  saveCustom(consent: Omit<CookieConsent, 'necessary' | 'version'>): void {
    this.saveConsent({
      necessary: true,
      analytics: consent.analytics,
      marketing: consent.marketing,
      version: 1,
    });
  }

  applyStoredConsent(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.forceLoadAllScripts) {
      this.applyConsent({
        necessary: true,
        analytics: true,
        marketing: true,
        version: 1,
      });

      return;
    }

    const consent = this.getConsent();

    if (!consent) {
      return;
    }

    this.applyConsent(consent);
  }

  private saveConsent(consent: CookieConsent): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    localStorage.setItem(this.storageKey, JSON.stringify(consent));

    this.applyConsent(
      this.forceLoadAllScripts
        ? {
          necessary: true,
          analytics: true,
          marketing: true,
          version: 1,
        }
        : consent,
    );
  }

  private initConsentMode(): void {
    window.dataLayer = window.dataLayer || [];

    window.gtag =
      window.gtag ||
      function (...args: any[]) {
        window.dataLayer.push(args);
      };

    window.gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
    });
  }

  private applyConsent(consent: CookieConsent): void {

    if (!this.isProduction) {
      return;
    }

    window.gtag('consent', 'update', {
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.marketing ? 'granted' : 'denied'
    });

    this.loadGoogleTagManager();
  }

  // private applyConsent(consent: CookieConsent): void {
  //   return;
  //
  //   if (!this.isProduction) {
  //     return;
  //   }
  //
  //   window.gtag('consent', 'update', {
  //     analytics_storage: consent.analytics ? 'granted' : 'denied',
  //     ad_storage: consent.marketing ? 'granted' : 'denied',
  //     ad_user_data: consent.marketing ? 'granted' : 'denied',
  //     ad_personalization: consent.marketing ? 'granted' : 'denied',
  //   });
  //
  //   if (consent.analytics) {
  //     this.loadGoogleAnalytics();
  //     this.loadHotjar();
  //     this.loadClarity();
  //   }
  //
  //   if (consent.marketing) {
  //     this.loadGoogleTagManager();
  //     this.loadMetaPixel();
  //   }
  // }

  private loadScript(id: string, src: string, onLoad?: () => void): void {
    const existingScript = this.document.getElementById(id) as HTMLScriptElement | null;

    if (existingScript) {
      onLoad?.();
      return;
    }

    const script = this.document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = src;

    if (onLoad) {
      script.onload = onLoad;
    }

    this.document.head.appendChild(script);
  }

  // private loadGoogleAnalytics(): void {
  //   this.loadScript(
  //     'mspadek-ga4',
  //     'https://www.googletagmanager.com/gtag/js?id=G-HW1GP1RW0E',
  //     () => {
  //       window.gtag('js', new Date());
  //       window.gtag('config', 'G-HW1GP1RW0E', {
  //         anonymize_ip: true,
  //         page_title: document.title,
  //         page_location: window.location.href,
  //         page_path: window.location.pathname,
  //       });
  //     },
  //   );
  // }

  private loadGoogleTagManager(): void {
    if (this.document.getElementById('mspadek-gtm')) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    this.loadScript(
      'mspadek-gtm',
      'https://www.googletagmanager.com/gtm.js?id=GTM-594JDCLP',
    );
  }

  // private loadHotjar(): void {
  //   if (this.document.getElementById('mspadek-hotjar')) {
  //     return;
  //   }
  //
  //   window.hj =
  //     window.hj ||
  //     function (...args: any[]) {
  //       (window.hj.q = window.hj.q || []).push(args);
  //     };
  //
  //   window._hjSettings = {
  //     hjid: 6486240,
  //     hjsv: 6,
  //   };
  //
  //   this.loadScript(
  //     'mspadek-hotjar',
  //     'https://static.hotjar.com/c/hotjar-6486240.js?sv=6',
  //   );
  // }

  // private loadClarity(): void {
  //   if (this.document.getElementById('mspadek-clarity')) {
  //     return;
  //   }
  //
  //   window.clarity =
  //     window.clarity ||
  //     function (...args: any[]) {
  //       (window.clarity.q = window.clarity.q || []).push(args);
  //     };
  //
  //   window.clarity('consentv2', {
  //     ad_storage: 'denied',
  //     analytics_storage: 'granted',
  //   });
  //
  //   this.loadScript(
  //     'mspadek-clarity',
  //     'https://www.clarity.ms/tag/wpx69ll9tq',
  //   );
  // }

  // private loadMetaPixel(): void {
  //   if (this.document.getElementById('mspadek-meta-pixel')) {
  //     return;
  //   }
  //
  //   const fbq: any = function (...args: any[]) {
  //     fbq.callMethod ? fbq.callMethod.apply(fbq, args) : fbq.queue.push(args);
  //   };
  //
  //   window.fbq = window.fbq || fbq;
  //   window._fbq = window._fbq || window.fbq;
  //
  //   window.fbq.push = window.fbq;
  //   window.fbq.loaded = true;
  //   window.fbq.version = '2.0';
  //   window.fbq.queue = window.fbq.queue || [];
  //
  //   this.loadScript(
  //     'mspadek-meta-pixel',
  //     'https://connect.facebook.net/en_US/fbevents.js',
  //   );
  //
  //   window.fbq('consent', 'grant');
  //   window.fbq('init', '1074550357402705');
  //   window.fbq('track', 'PageView');
  // }

  hasConsentDecision(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return true;
    }
    return !!localStorage.getItem(this.storageKey);
  }
}
